import type { CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { hasActiveTextSelection } from "@/shared/textSelection";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import type { Order } from "../types";
import type { OrderMarginRow } from "../economicSummaryApi";
import { isBonificoDaVerificare } from "./BonificoButton";
import { MarginNetLine } from "./MarginNetLine";
import {
  CardActions,
  CopyButton,
  CustomerContactRow,
  PhotoCell,
  ShipDeadlineChip,
  SkuChips,
  SupplierBadges,
  formatArrivo,
  orderMarketplaceLogo,
  shipDeadlineUrgency,
} from "./OrderCard";
import { SpedizioneCell } from "./SpedizioneCell";
import { statiDellOrdine } from "./StatiCell";
import { BADGE_TONI } from "./statoToni";

interface OrderCardMobileProps {
  order: Order;
  isSelected: boolean;
  pickupMailBusy?: boolean;
  onToggleSelect: (id: string) => void;
  onSendPickupMail?: (order: Order) => Promise<void>;
  onShippingSaved?: () => void;
  onCreateReturn?: (orderRef: string, kind: "reso" | "sostituzione") => void;
  returnBusy?: boolean;
  onOrderToSupplier?: (order: Order) => void;
  orderToSupplierBusy?: boolean;
  marginRow?: OrderMarginRow | null;
}

/** Il bordo della card dice l'urgenza, come in lista desktop e con le stesse
 *  precedenze: bonifico > scaduta > oggi > magazzino BS COM > selezionata. */
function cornice(order: Order, isSelected: boolean): string {
  const urgenza = shipDeadlineUrgency(order.shipDeadline, order.state);
  if (isBonificoDaVerificare(order))
    return "border-[#EAB308] [background:color-mix(in_oklab,#EAB308_7%,#FFFFFF)] shadow-[0_0_0_2px_rgba(234,179,8,0.4)]";
  if (urgenza === "scaduta")
    return "border-red [background:color-mix(in_oklab,#B5443A_12%,#FFFFFF)] shadow-[0_0_0_2px_rgba(181,68,58,0.7)]";
  if (urgenza === "oggi")
    return "border-red [background:color-mix(in_oklab,#B5443A_6%,#FFFFFF)] shadow-[0_0_0_1.5px_rgba(181,68,58,0.45)]";
  if (isSelected) return "border-accent [background:color-mix(in_oklab,#C96442_5%,#FFFFFF)]";
  return "border-line bg-card";
}

/**
 * Card ordine del telefono (redesign 2026-10-23, Pixel 9 a 412px).
 *
 * Stessi dati e stessi tasti della riga desktop (`OrderCard`), impilati per
 * importanza invece che in otto colonne: chi e' (numero, canale, totale), cosa
 * (prodotto, fornitore, SKU), a che punto (stati), per chi (cliente),
 * come parte (spedizione), cosa si puo' fare (tasti). Un tocco sulla card apre
 * la pagina ordine nella STESSA scheda: sul telefono una scheda nuova e' un
 * vicolo cieco, il «indietro» di Android riporta alla lista.
 */
export function OrderCardMobile({
  order,
  isSelected,
  pickupMailBusy,
  onToggleSelect,
  onSendPickupMail,
  onShippingSaved,
  onCreateReturn,
  returnBusy,
  onOrderToSupplier,
  orderToSupplierBusy,
  marginRow,
}: OrderCardMobileProps) {
  const navigate = useNavigate();
  const arrivo = formatArrivo(order.createdAtPoleepo);
  const totale = `€ ${order.total.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
  const stati = statiDellOrdine(order);
  const haFoto =
    Boolean(order.thumbnail) || (order.items ?? []).filter((it) => it.id != null).length > 1;
  const apri = () => void navigate(`/ordini/${encodeURIComponent(order.id)}`);

  return (
    <article
      data-testid={`order-card-${order.id}`}
      data-ship-today={shipDeadlineUrgency(order.shipDeadline, order.state) === "oggi" || undefined}
      data-ship-late={
        shipDeadlineUrgency(order.shipDeadline, order.state) === "scaduta" || undefined
      }
      className={[
        "relative mb-2.5 overflow-hidden rounded-2xl border transition-[border-color,box-shadow] duration-150",
        cornice(order, isSelected),
        order.inWarehouse ? "border-l-[6px] border-l-teal" : "",
      ].join(" ")}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={`Ordine ${order.id}`}
        className="flex flex-col gap-2.5 px-3.5 pb-3 pt-2.5"
        onClick={(e) => {
          const t = e.target as HTMLElement;
          if (t.closest("[data-no-open]") || t.closest("button") || t.closest("a")) return;
          if (hasActiveTextSelection()) return;
          apri();
        }}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          const t = e.target as HTMLElement;
          if (t.closest("[data-no-open]") || t.closest("button") || t.closest("a")) return;
          e.preventDefault();
          apri();
        }}
      >
        {/* Riga 1: selezione · canale · numero · totale. */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-no-open
            aria-label={`Seleziona ordine ${order.id}`}
            aria-pressed={isSelected}
            data-testid={`checkbox-${order.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(order.id);
            }}
            className="-ml-2 grid h-11 w-11 shrink-0 place-items-center"
          >
            <span
              className={[
                "grid h-[22px] w-[22px] place-items-center rounded-md border-2 transition-colors",
                isSelected ? "border-accent bg-accent" : "border-line-2 bg-card",
              ].join(" ")}
            >
              <svg
                className={`h-3.5 w-3.5 fill-none stroke-white stroke-[3] [stroke-linecap:round] [stroke-linejoin:round] ${isSelected ? "opacity-100" : "opacity-0"}`}
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
          </button>
          <MarketplaceLogo {...orderMarketplaceLogo(order)} size={20} className="shrink-0" />
          <span className="min-w-0 truncate font-display text-[16px] font-semibold tracking-[-0.01em] text-ink">
            {order.id}
          </span>
          <CopyButton text={order.id} label="Copia ID ordine" />
          <span className="ml-auto shrink-0 whitespace-nowrap font-display text-[17px] font-semibold tabular-nums tracking-[-0.02em] text-ink">
            {totale}
          </span>
        </div>

        {/* Riga 2: foto (se c'e') · prodotto · fornitore e SKU. */}
        <div className="flex gap-3">
          {haFoto && <PhotoCell order={order} misura="w-16 h-16" />}
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <p className="line-clamp-2 text-[15px] font-semibold leading-snug text-ink">
              {order.productName}
            </p>
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <SupplierBadges order={order} />
              {order.items && order.items.length > 0 && (
                <SkuChips items={order.items} totalQuantity={order.totalQuantity} />
              )}
              {order.supplierOrderRef && (
                <span className="text-[12.5px] font-medium text-ink">
                  N° {order.supplierOrderRef}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Riga 3: gli stati, in pillole a parole intere (niente maiuscolo:
            si legge meglio, e la riga va a capo invece di tagliare). */}
        {stati.length > 0 && (
          <ul className="flex flex-wrap gap-1.5" aria-label="Stati dell'ordine">
            {stati.map((s) => (
              <li
                key={s.key}
                title={s.title}
                data-testid={s.testId}
                style={{ "--tono": BADGE_TONI[s.tone] } as CSSProperties}
                className="inline-flex h-7 items-center gap-1 rounded-full bg-[color:var(--tono)] px-2.5 text-[12.5px] font-semibold text-white"
              >
                {s.label}
                {s.dettaglio && <span className="font-medium opacity-90">· {s.dettaglio}</span>}
              </li>
            ))}
          </ul>
        )}

        {/* Riga 4: cliente. */}
        <div className="flex flex-col gap-0.5 border-t border-line pt-2.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[14.5px] font-semibold text-ink">
              {order.customer.name}
            </span>
            {arrivo && <span className="shrink-0 text-[12px] tabular-nums text-ink">{arrivo}</span>}
          </div>
          <span className="truncate text-[13.5px] text-ink">
            {(order.customer.country || "IT").toUpperCase()} · {order.customer.address} ·{" "}
            {order.customer.city}
          </span>
          <div className="[&_a]:text-[13.5px] [&_button]:text-[13.5px]">
            <CustomerContactRow phone={order.customer.phone} email={order.customer.email} />
          </div>
        </div>

        {/* Riga 5: spedizione (corriere, colli, scadenza, tracking). */}
        <div className="[&_*]:max-w-full">
          <SpedizioneCell
            order={order}
            scadenza={<ShipDeadlineChip deadline={order.shipDeadline} state={order.state} />}
          />
        </div>

        {(order.costo > 0 || marginRow) && (
          <div className="flex flex-wrap items-center gap-x-2 text-[12.5px] text-ink">
            {order.costo > 0 && (
              <span className="whitespace-nowrap">
                costo € {order.costo.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                {order.margin ? ` · ${order.margin}` : ""}
              </span>
            )}
            <MarginNetLine row={marginRow} />
          </div>
        )}
      </div>

      {/* Tasti in griglia da due (redesign 2026-10-27): la fila che scorreva di
          lato tagliava l'ultimo tasto a meta' («Sos…») e non diceva che ce
          n'erano altri. Cosi' si vedono tutti, ognuno largo mezzo schermo. */}
      <div
        data-no-open
        className="grid grid-cols-2 gap-2 border-t border-line bg-paper/60 px-3.5 py-3 empty:hidden [&>*]:w-full [&>*>button]:w-full [&>*>button]:justify-center [&>button]:justify-center"
      >
        <CardActions
          order={order}
          pickupMailBusy={pickupMailBusy}
          returnBusy={returnBusy}
          onSendPickupMail={onSendPickupMail}
          onCreateReturn={onCreateReturn}
          onShippingSaved={onShippingSaved}
          onOrderToSupplier={onOrderToSupplier}
          orderToSupplierBusy={orderToSupplierBusy}
        />
      </div>
    </article>
  );
}
